import React, { useEffect, useState } from "react";
import { Input, Typography, Button, Divider, Checkbox, DatePicker } from "antd";
import dayjs from "dayjs";
import {
  FileTextOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { ParsedResumeContent, WhatsAppConsent } from "../../Interface/Index";
import "./ResumeReviewForm.scss";

const { TextArea } = Input;

interface ResumeReviewFormProps {
  formattedResume: ParsedResumeContent | null;
  onFormChange: (data: ParsedResumeContent) => void;
  onReupload: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const toDayjsMonth = (value: any): dayjs.Dayjs | null => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

// Comma-separated editable text component
const CommaSeparatedInput: React.FC<{
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}> = ({ values, onChange, placeholder = "Enter comma-separated values" }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    // Split by comma, trim whitespace, and filter empty strings
    const newValues = text
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    onChange(newValues);
  };

  return (
    <Input
      value={values.join(", ")}
      onChange={handleChange}
      placeholder={placeholder}
    />
  );
};

const ResumeReviewForm: React.FC<ResumeReviewFormProps> = ({
  formattedResume,
  onFormChange,
  onReupload,
  onConfirm,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<ParsedResumeContent>({
    basicDetails: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      address: "",
      whatsAppConsent: { consent: true, timestamp: new Date(), history: [] },
    },
    socialLinks: {
      linkedin: "",
      github: "",
      personalPortfolio: "",
    },
    summary: "",
    experience: [],
    education: [],
    skills: [],
    projects: [],
    certifications: [],
    achievements: [],
    languages: [],
    otherDetails: "",
  });
  console.log("ResumeReviewForm: form Data", formData);
  console.log("ResumeReviewForm: form Data", formData);

  // Initialize form with formatted resume data
  useEffect(() => {
    if (formattedResume) {
      setFormData({
        ...formattedResume,
        basicDetails: {
          ...formattedResume.basicDetails,
          whatsAppConsent: formattedResume.basicDetails?.whatsAppConsent ?? {
            consent: true,
            timestamp: new Date(),
            history: [],
          },
        },
      });
    } else {
      // Initialize with empty basicDetails to ensure email and phone fields are always available
      setFormData({
        basicDetails: {
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          address: "",
          whatsAppConsent: {
            consent: true,
            timestamp: new Date(),
            history: [],
          },
        },
        socialLinks: {
          linkedin: "",
          github: "",
          personalPortfolio: "",
        },
        skills: [],
        experience: [],
        education: [],
        projects: [],
        certifications: [],
        achievements: [],
        languages: [],
        otherDetails: "",
        summary: "",
      });
    }
  }, [formattedResume]);

  // Notify parent of changes
  useEffect(() => {
    onFormChange(formData);
  }, [formData]);

  // Helper to update nested fields
  const updateBasicDetails = (
    field: string,
    value: string | boolean | WhatsAppConsent,
  ) => {
    setFormData((prev) => ({
      ...prev,
      basicDetails: { ...prev.basicDetails, [field]: value },
    }));
  };

  const updateSocialLinks = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [field]: value },
    }));
  };

  const updateExperience = (
    index: number,
    field: string,
    value: string | null,
  ) => {
    setFormData((prev) => {
      const experience = [...(prev.experience || [])];
      experience[index] = { ...experience[index], [field]: value };
      return { ...prev, experience };
    });
  };

  const addExperience = () => {
    setFormData((prev) => ({
      ...prev,
      experience: [
        ...(prev.experience || []),
        {
          companyName: "",
          designation: "",
          description: "",
          location: "",
          from: "",
          to: undefined,
        },
      ],
    }));
  };

  const removeExperience = (index: number) => {
    setFormData((prev) => {
      const experience = [...(prev.experience || [])];
      experience.splice(index, 1);
      return { ...prev, experience };
    });
  };

  const updateEducation = (
    index: number,
    field: string,
    value: string | null,
  ) => {
    setFormData((prev) => {
      const education = [...(prev.education || [])];
      education[index] = { ...education[index], [field]: value };
      return { ...prev, education };
    });
  };

  const addEducation = () => {
    setFormData((prev) => ({
      ...prev,
      education: [
        ...(prev.education || []),
        {
          degree: "",
          school: "",
          location: "",
          description: "",
          from: "",
          to: undefined,
        },
      ],
    }));
  };

  const removeEducation = (index: number) => {
    setFormData((prev) => {
      const education = [...(prev.education || [])];
      education.splice(index, 1);
      return { ...prev, education };
    });
  };

  const addProject = () => {
    setFormData((prev) => ({
      ...prev,
      projects: [
        ...(prev.projects || []),
        {
          title: "",
          description: "",
          technologies: [],
          githubLink: null,
          liveLink: null,
        },
      ],
    }));
  };

  const removeProject = (index: number) => {
    setFormData((prev) => {
      const projects = [...(prev.projects || [])];
      projects.splice(index, 1);
      return { ...prev, projects };
    });
  };

  const updateCertification = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    setFormData((prev) => {
      const certifications = [...(prev.certifications || [])];
      certifications[index] = { ...certifications[index], [field]: value };
      return { ...prev, certifications };
    });
  };

  const addCertification = () => {
    setFormData((prev) => ({
      ...prev,
      certifications: [
        ...(prev.certifications || []),
        { name: "", issuer: "", year: new Date().getFullYear() },
      ],
    }));
  };

  const removeCertification = (index: number) => {
    setFormData((prev) => {
      const certifications = [...(prev.certifications || [])];
      certifications.splice(index, 1);
      return { ...prev, certifications };
    });
  };

  const updateProject = (index: number, field: string, value: any) => {
    setFormData((prev) => {
      const projects = [...(prev.projects || [])];
      projects[index] = { ...projects[index], [field]: value };
      return { ...prev, projects };
    });
  };

  // Determine which sections have data
  const hasBasicDetails =
    formData.basicDetails && Object.values(formData.basicDetails).some(Boolean);
  const hasSocialLinks =
    formData.socialLinks && Object.values(formData.socialLinks).some(Boolean);
  const hasSummary = !!formData.summary;
  const hasSkills = formData.skills && formData.skills.length > 0;
  const hasLanguages = formData.languages && formData.languages.length > 0;
  const hasAchievements =
    formData.achievements && formData.achievements.length > 0;
  const hasOtherDetails = !!formData.otherDetails;

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Validation helpers
  const isEmailValid = (email: string | undefined): boolean => {
    if (!email || email.trim().length === 0) return false;
    return emailRegex.test(email.trim());
  };

  const isPhoneValid = (phone: string | undefined): boolean => {
    if (!phone || phone.trim().length === 0) return false;
    // Remove common formatting characters for validation
    const cleanedPhone = phone.replace(/[\s\-\\(\\)\\+]/g, "");
    // Check if it has at least 7 digits (minimum for a valid phone number)
    return /^\d{7,15}$/.test(cleanedPhone);
  };

  const emailError =
    !formData.basicDetails?.email ||
    formData.basicDetails?.email.trim().length === 0
      ? "Email is required"
      : !isEmailValid(formData.basicDetails?.email)
        ? "Please enter a valid email address"
        : null;

  const phoneError =
    !formData.basicDetails?.phoneNumber ||
    formData.basicDetails?.phoneNumber.trim().length === 0
      ? "Phone number is required"
      : !isPhoneValid(formData.basicDetails?.phoneNumber || "")
        ? "Please enter a valid phone number"
        : null;

  // Validation: Email and phone number are mandatory and must be valid
  const isFormValid =
    isEmailValid(formData.basicDetails?.email) &&
    isPhoneValid(formData.basicDetails?.phoneNumber || "") &&
    (formData.basicDetails?.firstName ||
      formData.summary ||
      (formData.skills && formData.skills.length > 0));

  return (
    <div className="resume-review-form">
      <div className="resume-review-form__header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Review Your Information
        </Typography.Title>
        <Typography.Text type="secondary">
          Please verify and edit the extracted information below
        </Typography.Text>
      </div>

      <div className="resume-review-form__content">
        {/* Personal Information - Always show email and phone number fields */}
        <div className="resume-review-form__section">
          <Typography.Title
            level={5}
            className="resume-review-form__section-title"
          >
            Personal Information
          </Typography.Title>
          <div className="resume-review-form__grid">
            <div className="resume-review-form__field">
              <label>First Name</label>
              <Input
                value={formData.basicDetails?.firstName || ""}
                onChange={(e) =>
                  updateBasicDetails("firstName", e.target.value)
                }
                placeholder="First name"
              />
            </div>
            <div className="resume-review-form__field">
              <label>Last Name</label>
              <Input
                value={formData.basicDetails?.lastName || ""}
                onChange={(e) => updateBasicDetails("lastName", e.target.value)}
                placeholder="Last name"
              />
            </div>
            <div className="resume-review-form__field">
              <label>
                Email <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                value={formData.basicDetails?.email || ""}
                onChange={(e) => updateBasicDetails("email", e.target.value)}
                placeholder="Email address"
                type="email"
                required
                status={emailError ? "error" : ""}
              />
              {emailError && (
                <Typography.Text
                  type="danger"
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  {emailError}
                </Typography.Text>
              )}
            </div>
            <div className="resume-review-form__field">
              <label>
                Phone Number <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                value={formData.basicDetails?.phoneNumber || ""}
                onChange={(e) =>
                  updateBasicDetails("phoneNumber", e.target.value)
                }
                placeholder="Phone number (e.g., +1 234-567-8900)"
                required
                status={phoneError ? "error" : ""}
              />
              {phoneError && (
                <Typography.Text
                  type="danger"
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  {phoneError}
                </Typography.Text>
              )}
            </div>
            <div className="resume-review-form__field resume-review-form__field--full">
              <Checkbox
                checked={
                  formData.basicDetails?.whatsAppConsent?.consent !== false
                }
                onChange={(e) =>
                  updateBasicDetails("whatsAppConsent", {
                    consent: e.target.checked,
                    timestamp: new Date(),
                    history: [],
                  })
                }
                style={{ display: "flex", alignItems: "center" }}
              >
                <img
                  src="/icons/whatsapp.svg"
                  alt="WhatsApp"
                  style={{
                    width: "20px",
                    marginRight: "8px",
                    verticalAlign: "middle",
                  }}
                />
                I agree to receive WhatsApp notifications
              </Checkbox>
            </div>
            {(formData.basicDetails?.address || hasBasicDetails) && (
              <div className="resume-review-form__field resume-review-form__field--full">
                <label>Address</label>
                <Input
                  value={formData.basicDetails?.address || ""}
                  onChange={(e) =>
                    updateBasicDetails("address", e.target.value)
                  }
                  placeholder="Address"
                />
              </div>
            )}
          </div>
        </div>

        {/* Social Links */}
        {hasSocialLinks && (
          <div className="resume-review-form__section">
            <Typography.Title
              level={5}
              className="resume-review-form__section-title"
            >
              Social Links
            </Typography.Title>
            <div className="resume-review-form__grid">
              {formData.socialLinks?.linkedin && (
                <div className="resume-review-form__field">
                  <label>LinkedIn</label>
                  <Input
                    value={formData.socialLinks?.linkedin || ""}
                    onChange={(e) =>
                      updateSocialLinks("linkedin", e.target.value)
                    }
                    placeholder="LinkedIn URL"
                  />
                </div>
              )}
              {formData.socialLinks?.github && (
                <div className="resume-review-form__field">
                  <label>GitHub</label>
                  <Input
                    value={formData.socialLinks?.github || ""}
                    onChange={(e) =>
                      updateSocialLinks("github", e.target.value)
                    }
                    placeholder="GitHub URL"
                  />
                </div>
              )}
              {formData.socialLinks?.personalPortfolio && (
                <div className="resume-review-form__field">
                  <label>Portfolio</label>
                  <Input
                    value={formData.socialLinks?.personalPortfolio || ""}
                    onChange={(e) =>
                      updateSocialLinks("personalPortfolio", e.target.value)
                    }
                    placeholder="Portfolio URL"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Professional Summary */}
        {hasSummary && (
          <div className="resume-review-form__section">
            <Typography.Title
              level={5}
              className="resume-review-form__section-title"
            >
              Professional Summary
            </Typography.Title>
            <TextArea
              value={formData.summary || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, summary: e.target.value }))
              }
              placeholder="Brief summary of your professional background..."
              rows={4}
            />
          </div>
        )}

        {/* Work Experience - always show section with all inputs and add more */}
        <div className="resume-review-form__section">
          <Typography.Title
            level={5}
            className="resume-review-form__section-title"
          >
            Work Experience
          </Typography.Title>
          {formData.experience?.map((exp, index) => {
            const isPresent = exp.to === undefined || exp.to === null;
            return (
              <div key={index} className="resume-review-form__item-card">
                <div className="resume-review-form__grid">
                  <div className="resume-review-form__field">
                    <label>Job Title</label>
                    <Input
                      value={exp.designation || ""}
                      onChange={(e) =>
                        updateExperience(index, "designation", e.target.value)
                      }
                      placeholder="Job title"
                    />
                  </div>
                  <div className="resume-review-form__field">
                    <label>Company</label>
                    <Input
                      value={exp.companyName || ""}
                      onChange={(e) =>
                        updateExperience(index, "companyName", e.target.value)
                      }
                      placeholder="Company name"
                    />
                  </div>
                  <div className="resume-review-form__field">
                    <label>Location</label>
                    <Input
                      value={exp.location || ""}
                      onChange={(e) =>
                        updateExperience(index, "location", e.target.value)
                      }
                      placeholder="Location"
                    />
                  </div>
                  <div className="resume-review-form__field">
                    <label>Start Date</label>
                    <DatePicker
                      picker="month"
                      format="MMM YYYY"
                      value={toDayjsMonth(exp.from)}
                      onChange={(_, dateString) =>
                        updateExperience(
                          index,
                          "from",
                          Array.isArray(dateString)
                            ? dateString[0] || null
                            : dateString || null,
                        )
                      }
                      placeholder="e.g. Jan 2020"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="resume-review-form__field">
                    <label>End Date</label>
                    <DatePicker
                      picker="month"
                      format="MMM YYYY"
                      value={isPresent ? null : toDayjsMonth(exp.to)}
                      onChange={(_, dateString) =>
                        updateExperience(
                          index,
                          "to",
                          Array.isArray(dateString)
                            ? dateString[0] || ""
                            : dateString || "",
                        )
                      }
                      placeholder="e.g. Dec 2024"
                      disabled={isPresent}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div
                    className="resume-review-form__field"
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      paddingBottom: "8px",
                    }}
                  >
                    <Checkbox
                      checked={isPresent}
                      onChange={(e) =>
                        updateExperience(
                          index,
                          "to",
                          e.target.checked ? null : "",
                        )
                      }
                    >
                      Present (current role)
                    </Checkbox>
                  </div>
                  <div className="resume-review-form__field resume-review-form__field--full">
                    <label>Description</label>
                    <TextArea
                      value={exp.description || ""}
                      onChange={(e) =>
                        updateExperience(index, "description", e.target.value)
                      }
                      placeholder="Job description and key responsibilities"
                      rows={3}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 8,
                  }}
                >
                  {formData.experience && formData.experience.length > 1 && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeExperience(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          <Button
            type="dashed"
            onClick={addExperience}
            block
            icon={<PlusOutlined />}
            style={{ marginTop: 8 }}
          >
            Add experience
          </Button>
        </div>

        {/* Education - always show with all inputs, Present, add/remove */}
        <div className="resume-review-form__section">
          <Typography.Title
            level={5}
            className="resume-review-form__section-title"
          >
            Education
          </Typography.Title>
          {formData.education?.map((edu, index) => {
            const isPresent = edu.to === undefined || edu.to === null;
            return (
              <div key={index} className="resume-review-form__item-card">
                <div className="resume-review-form__grid">
                  <div className="resume-review-form__field">
                    <label>Degree</label>
                    <Input
                      value={edu.degree || ""}
                      onChange={(e) =>
                        updateEducation(index, "degree", e.target.value)
                      }
                      placeholder="Degree"
                    />
                  </div>
                  <div className="resume-review-form__field">
                    <label>School/University</label>
                    <Input
                      value={edu.school || ""}
                      onChange={(e) =>
                        updateEducation(index, "school", e.target.value)
                      }
                      placeholder="School or university"
                    />
                  </div>
                  <div className="resume-review-form__field">
                    <label>Location</label>
                    <Input
                      value={edu.location || ""}
                      onChange={(e) =>
                        updateEducation(index, "location", e.target.value)
                      }
                      placeholder="Location"
                    />
                  </div>
                  <div className="resume-review-form__field">
                    <label>Start Date</label>
                    <DatePicker
                      picker="month"
                      format="MMM YYYY"
                      value={toDayjsMonth(edu.from)}
                      onChange={(_, dateString) =>
                        updateEducation(
                          index,
                          "from",
                          Array.isArray(dateString)
                            ? dateString[0] || null
                            : dateString || null,
                        )
                      }
                      placeholder="e.g. Sep 2016"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="resume-review-form__field">
                    <label>End Date</label>
                    <DatePicker
                      picker="month"
                      format="MMM YYYY"
                      value={isPresent ? null : toDayjsMonth(edu.to)}
                      onChange={(_, dateString) =>
                        updateEducation(
                          index,
                          "to",
                          Array.isArray(dateString)
                            ? dateString[0] || ""
                            : dateString || "",
                        )
                      }
                      placeholder="e.g. May 2020"
                      disabled={isPresent}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div
                    className="resume-review-form__field"
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      paddingBottom: "8px",
                    }}
                  >
                    <Checkbox
                      checked={isPresent}
                      onChange={(e) =>
                        updateEducation(
                          index,
                          "to",
                          e.target.checked ? null : "",
                        )
                      }
                    >
                      Present (currently studying)
                    </Checkbox>
                  </div>
                  <div className="resume-review-form__field resume-review-form__field--full">
                    <label>Description</label>
                    <TextArea
                      value={edu.description || ""}
                      onChange={(e) =>
                        updateEducation(index, "description", e.target.value)
                      }
                      placeholder="Relevant coursework, achievements"
                      rows={2}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 8,
                  }}
                >
                  {formData.education && formData.education.length > 1 && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeEducation(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          <Button
            type="dashed"
            onClick={addEducation}
            block
            icon={<PlusOutlined />}
            style={{ marginTop: 8 }}
          >
            Add education
          </Button>
        </div>

        {/* Skills */}
        {hasSkills && (
          <div className="resume-review-form__section">
            <Typography.Title
              level={5}
              className="resume-review-form__section-title"
            >
              Skills
            </Typography.Title>
            <CommaSeparatedInput
              values={formData.skills || []}
              onChange={(skills) =>
                setFormData((prev) => ({ ...prev, skills }))
              }
              placeholder="e.g., JavaScript, React, Node.js"
            />
          </div>
        )}

        {/* Languages */}
        {hasLanguages && (
          <div className="resume-review-form__section">
            <Typography.Title
              level={5}
              className="resume-review-form__section-title"
            >
              Languages
            </Typography.Title>
            <CommaSeparatedInput
              values={formData.languages || []}
              onChange={(languages) =>
                setFormData((prev) => ({ ...prev, languages }))
              }
              placeholder="e.g., English, Spanish, French"
            />
          </div>
        )}

        {/* Certifications */}
        <div className="resume-review-form__section">
          <Typography.Title
            level={5}
            className="resume-review-form__section-title"
          >
            Certifications
          </Typography.Title>
          {formData.certifications?.map((cert, index) => (
            <div key={index} className="resume-review-form__item-card">
              <div className="resume-review-form__grid">
                <div className="resume-review-form__field">
                  <label>Name</label>
                  <Input
                    value={cert.name || ""}
                    onChange={(e) =>
                      updateCertification(index, "name", e.target.value)
                    }
                    placeholder="Certification name"
                  />
                </div>
                <div className="resume-review-form__field">
                  <label>Issuer</label>
                  <Input
                    value={cert.issuer || ""}
                    onChange={(e) =>
                      updateCertification(index, "issuer", e.target.value)
                    }
                    placeholder="Issuing organization"
                  />
                </div>
                <div className="resume-review-form__field">
                  <label>Year</label>
                  <Input
                    value={cert.year || ""}
                    onChange={(e) =>
                      updateCertification(index, "year", e.target.value)
                    }
                    placeholder="Year"
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 8,
                }}
              >
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeCertification(index)}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="dashed"
            onClick={addCertification}
            block
            icon={<PlusOutlined />}
            style={{ marginTop: 8 }}
          >
            Add certification
          </Button>
        </div>

        {/* Projects - always show with all inputs, add/remove */}
        <div className="resume-review-form__section">
          <Typography.Title
            level={5}
            className="resume-review-form__section-title"
          >
            Projects
          </Typography.Title>
          {formData.projects?.map((project, index) => (
            <div key={index} className="resume-review-form__item-card">
              <div className="resume-review-form__grid">
                <div className="resume-review-form__field resume-review-form__field--full">
                  <label>Title</label>
                  <Input
                    value={project.title || ""}
                    onChange={(e) =>
                      updateProject(index, "title", e.target.value)
                    }
                    placeholder="Project title"
                  />
                </div>
                <div className="resume-review-form__field resume-review-form__field--full">
                  <label>Description</label>
                  <TextArea
                    value={project.description || ""}
                    onChange={(e) =>
                      updateProject(index, "description", e.target.value)
                    }
                    placeholder="Project description"
                    rows={3}
                  />
                </div>
                <div className="resume-review-form__field resume-review-form__field--full">
                  <label>Technologies</label>
                  <CommaSeparatedInput
                    values={project.technologies || []}
                    onChange={(technologies) =>
                      updateProject(index, "technologies", technologies)
                    }
                    placeholder="e.g., React, TypeScript, AWS"
                  />
                </div>
                <div className="resume-review-form__field">
                  <label>GitHub Link</label>
                  <Input
                    value={project.githubLink || ""}
                    onChange={(e) =>
                      updateProject(index, "githubLink", e.target.value)
                    }
                    placeholder="GitHub URL"
                  />
                </div>
                <div className="resume-review-form__field">
                  <label>Live Link</label>
                  <Input
                    value={project.liveLink || ""}
                    onChange={(e) =>
                      updateProject(index, "liveLink", e.target.value)
                    }
                    placeholder="Live URL"
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 8,
                }}
              >
                {formData.projects && formData.projects.length > 1 && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeProject(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button
            type="dashed"
            onClick={addProject}
            block
            icon={<PlusOutlined />}
            style={{ marginTop: 8 }}
          >
            Add project
          </Button>
        </div>

        {/* Achievements */}
        {hasAchievements && (
          <div className="resume-review-form__section">
            <Typography.Title
              level={5}
              className="resume-review-form__section-title"
            >
              Achievements
            </Typography.Title>
            <TextArea
              value={(formData.achievements || []).join("\n")}
              onChange={(e) => {
                const text = e.target.value;
                const achievements = text
                  .split("\n")
                  .map((v) => v.trim())
                  .filter((v) => v.length > 0);
                setFormData((prev) => ({ ...prev, achievements }));
              }}
              placeholder="Enter achievements (one per line)"
              rows={3}
            />
          </div>
        )}

        {/* Other Details */}
        {hasOtherDetails && (
          <div className="resume-review-form__section">
            <Typography.Title
              level={5}
              className="resume-review-form__section-title"
            >
              Additional Information
            </Typography.Title>
            <TextArea
              value={formData.otherDetails || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  otherDetails: e.target.value,
                }))
              }
              placeholder="Any other relevant information..."
              rows={3}
            />
          </div>
        )}
      </div>

      <Divider style={{ margin: "16px 0" }} />

      {/* Footer Actions */}
      <div className="resume-review-form__footer">
        <Button
          type="text"
          icon={<FileTextOutlined />}
          onClick={onReupload}
          className="resume-review-form__reupload-btn"
        >
          Re-upload CV
        </Button>
        <Button
          type="primary"
          onClick={onConfirm}
          disabled={!isFormValid || isLoading}
          loading={isLoading}
          className="resume-review-form__confirm-btn"
        >
          Confirm & Continue
        </Button>
      </div>
    </div>
  );
};

export default ResumeReviewForm;
